import React, { useMemo } from "react";
import Dropdown from "../../common/dropDown";
import StudyDatePicker from "./StudyDatePicker";
import {
  STUDY_TYPE_OPTIONS,
  formatStudyDuration,
  getTomorrowIsoDate,
  isValidProtocolVersion,
  parseIsoDate,
} from "./studyFormMetadataUtils";
import { parseReferenceDateInput } from "../isf/isfSiteSetupService";

export function StudyStartDateField({
  values,
  onChange,
  isCreating = false,
  disabled = false,
}) {
  const startMinDate = useMemo(
    () => (isCreating ? parseReferenceDateInput(getTomorrowIsoDate()) : null),
    [isCreating],
  );

  return (
    <div className="study-form-field">
      <label>Start Date</label>
      <StudyDatePicker
        value={values.startDate || ""}
        onChange={(next) => onChange("startDate", next)}
        disabled={disabled}
        minDate={startMinDate}
      />
    </div>
  );
}

export function StudyEndDateField({
  values,
  onChange,
  disabled = false,
}) {
  const durationLabel = formatStudyDuration(values.startDate, values.endDate);

  const endMinDate = useMemo(() => {
    const start = parseIsoDate(values.startDate);
    if (!start) return null;
    const next = new Date(start);
    next.setDate(next.getDate() + 1);
    return next;
  }, [values.startDate]);

  return (
    <div className="study-form-field">
      <label>End Date</label>
      <StudyDatePicker
        value={values.endDate || ""}
        onChange={(next) => onChange("endDate", next)}
        disabled={disabled}
        minDate={endMinDate}
      />
      {durationLabel ? (
        <p className="study-date-range__duration">Duration: {durationLabel}</p>
      ) : null}
    </div>
  );
}

export function StudyTypeField({ values, onChange, disabled = false }) {
  return (
    <div className="study-form-field">
      <label>Study Type</label>
      <Dropdown
        options={STUDY_TYPE_OPTIONS}
        isMulti={false}
        selected={values.studyType}
        placeholder="Select study type"
        onSelect={(opt) => onChange("studyType", opt || null)}
        disabled={disabled}
      />
    </div>
  );
}

export function StudyProtocolNumberField({ values, onChange, disabled = false }) {
  return (
    <div className="study-form-field">
      <label>Protocol Number</label>
      <input
        type="text"
        className="form-control"
        name="protocolNumber"
        value={values.protocolNumber || ""}
        disabled={disabled}
        onChange={(e) => onChange("protocolNumber", e.target.value)}
      />
    </div>
  );
}

export function StudyProtocolVersionField({ values, onChange, disabled = false }) {
  const versionInvalid =
    String(values.protocolVersion || "").trim() &&
    !isValidProtocolVersion(values.protocolVersion);

  return (
    <div className="study-form-field">
      <label>Protocol Version</label>
      <input
        type="text"
        className={`form-control${versionInvalid ? " is-invalid" : ""}`}
        name="protocolVersion"
        value={values.protocolVersion || ""}
        placeholder="e.g. 1.0"
        disabled={disabled}
        onChange={(e) => onChange("protocolVersion", e.target.value)}
      />
      {versionInvalid ? (
        <span className="study-form-inline-error">
          Use numbers and a dot only (e.g. 1.0).
        </span>
      ) : null}
    </div>
  );
}

export function StudyDescriptionField({ values, onChange, disabled = false }) {
  return (
    <div className="study-form-field study-form-field--span-row">
      <label>Study Description / Abstract</label>
      <textarea
        className="form-control study-description-input"
        name="studyDescription"
        rows={4}
        value={values.studyDescription || ""}
        disabled={disabled}
        onChange={(e) => onChange("studyDescription", e.target.value)}
      />
    </div>
  );
}

function StudyMetadataFields({
  values,
  onChange,
  isCreating = false,
  disabled = false,
}) {
  return (
    <>
      <StudyStartDateField
        values={values}
        onChange={onChange}
        isCreating={isCreating}
        disabled={disabled}
      />
      <StudyEndDateField values={values} onChange={onChange} disabled={disabled} />
      <StudyTypeField values={values} onChange={onChange} disabled={disabled} />
      <StudyProtocolNumberField
        values={values}
        onChange={onChange}
        disabled={disabled}
      />
      <StudyProtocolVersionField
        values={values}
        onChange={onChange}
        disabled={disabled}
      />
      <StudyDescriptionField values={values} onChange={onChange} disabled={disabled} />
    </>
  );
}

export default StudyMetadataFields;
